import * as THREE from "three";
import type { CowryGeometryParams } from "../core/types";

const PROFILE_SEGMENTS = 64;
const RADIAL_SEGMENTS = 32;
const FIBONACCI_STATIONS = [1, 2, 3, 5, 8, 13] as const;

export interface LathePropulsionOrgan {
  root: THREE.Group;
  mesh: THREE.Mesh<THREE.BufferGeometry, THREE.MeshPhysicalMaterial>;
  filament: THREE.Mesh<THREE.TubeGeometry, THREE.MeshPhysicalMaterial>;
}

export interface LathePropulsionParams {
  axisAlignment: "ventral-fold" | "shell-endpoints";
  foldSeating: number;
  diameter: number;
  middleBulge: number;
  chamberDetail: number;
  opacity: number;
  filamentOpacity: number;
  axisOffset: number;
  endpointInset: number;
  profileEasing: number;
  filamentClearance: number;
}

export const defaultLathePropulsionParams: LathePropulsionParams = {
  axisAlignment: "shell-endpoints",
  foldSeating: 0,
  diameter: 2.3,
  middleBulge: 4,
  chamberDetail: 1,
  opacity: 0.35,
  filamentOpacity: 0.35,
  axisOffset: 0,
  endpointInset: 0.005,
  profileEasing: 3,
  filamentClearance: 0,
};

export function buildLathePropulsionOrgan(
  params: CowryGeometryParams,
  itemSeed: string,
  foldEmphasis: number,
  organParams: LathePropulsionParams = defaultLathePropulsionParams,
): LathePropulsionOrgan {
  validateParams(organParams);
  const root = new THREE.Group();
  root.name = "lathe-propulsion-organ";

  const length = params.shellLength * (1 - organParams.endpointInset * 2);
  const thickness = 0.88 + hashUnit(`${itemSeed}:lathe-thickness`) * 0.18;
  const maxRadius = params.shellHeight * 0.075 * thickness * organParams.diameter;
  const profile = buildProfile(length, maxRadius, itemSeed, organParams);
  const geometry = new THREE.LatheGeometry(profile, RADIAL_SEGMENTS);
  geometry.rotateZ(Math.PI * 0.5);
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();

  const mesh = new THREE.Mesh(geometry, createMaterials(organParams.opacity));
  mesh.name = "lathe-propulsion-body";
  const seatingY = organParams.axisAlignment === "shell-endpoints"
    ? shellEndpointAxisY() + organParams.axisOffset * params.shellHeight - maxRadius * organParams.foldSeating
    : ventralAxisY(params, foldEmphasis) - maxRadius * (organParams.foldSeating - 0.5) * 2;
  mesh.position.y = seatingY;
  mesh.castShadow = true;
  const filament = buildFilament(length, maxRadius, itemSeed, organParams);
  filament.position.y = seatingY;
  root.add(mesh, filament);
  return { root, mesh, filament };
}

function buildProfile(
  length: number,
  maxRadius: number,
  itemSeed: string,
  organParams: LathePropulsionParams,
): THREE.Vector2[] {
  const phase = hashUnit(itemSeed) * Math.PI * 2;
  const stations = FIBONACCI_STATIONS.map((value) => value / 14);
  const points: THREE.Vector2[] = [];

  for (let index = 0; index <= PROFILE_SEGMENTS; index += 1) {
    const t = index / PROFILE_SEGMENTS;
    const envelope = Math.pow(Math.sin(Math.PI * t), organParams.profileEasing);
    let chamber = 0;
    for (const [stationIndex, station] of stations.entries()) {
      const mirrored = stationIndex % 2 === 0 ? station : 1 - station;
      const width = 0.018 + station * 0.012;
      const distance = (t - mirrored) / width;
      chamber += Math.exp(-distance * distance)
        * (0.14 + station * 0.13)
        * (0.9 + 0.1 * Math.sin(phase + stationIndex * 2.4));
    }
    const turbineRipple = 0.045 * Math.sin(t * Math.PI * 16 + phase) * envelope;
    const middle = Math.exp(-Math.pow((t - 0.5) / 0.28, 2));
    const smoothBody = 0.64 + organParams.middleBulge * 0.34 * middle;
    const radius = index === 0 || index === PROFILE_SEGMENTS
      ? 0
      : maxRadius * envelope * (
        smoothBody + (chamber + turbineRipple) * organParams.chamberDetail
      );
    points.push(new THREE.Vector2(Math.max(0, radius), (t - 0.5) * length));
  }
  return points;
}

function createMaterials(opacity: number): THREE.MeshPhysicalMaterial {
  return new THREE.MeshPhysicalMaterial({
    side: THREE.DoubleSide,
    name: "lathe-dark-ethereal-body",
    color: 0x17151d,
    roughness: 0.25,
    metalness: 0.28,
    clearcoat: 0.7,
    clearcoatRoughness: 0.16,
    opacity,
    transparent: opacity < 1,
    depthWrite: opacity >= 0.72,
  });
}

function buildFilament(
  length: number,
  maxRadius: number,
  itemSeed: string,
  organParams: LathePropulsionParams,
): THREE.Mesh<THREE.TubeGeometry, THREE.MeshPhysicalMaterial> {
  const phase = hashUnit(`${itemSeed}:filament-phase`) * Math.PI * 2;
  const points: THREE.Vector3[] = [];
  const segments = 256;
  for (let index = 0; index <= segments; index += 1) {
    const t = index / segments;
    const surfaceRadius = sampleProfileRadius(t, maxRadius, itemSeed, organParams);
    const angle = phase + t * Math.PI * 2 * 13;
    const radius = surfaceRadius + maxRadius * organParams.filamentClearance;
    points.push(new THREE.Vector3(
      (t - 0.5) * length,
      Math.cos(angle) * radius,
      Math.sin(angle) * radius,
    ));
  }
  const curve = new THREE.CatmullRomCurve3(points);
  const geometry = new THREE.TubeGeometry(curve, segments, maxRadius * 0.026, 8, false);
  const opacity = organParams.filamentOpacity;
  const material = new THREE.MeshPhysicalMaterial({
    name: "lathe-ethereal-filament",
    color: 0xa8d5d0,
    roughness: 0.12,
    metalness: 0.84,
    clearcoat: 0.78,
    clearcoatRoughness: 0.1,
    opacity,
    transparent: opacity < 1,
    depthWrite: opacity >= 0.72,
  });
  const filament = new THREE.Mesh(geometry, material);
  filament.name = "lathe-13-turn-filament";
  filament.castShadow = true;
  return filament;
}

function sampleProfileRadius(
  t: number,
  maxRadius: number,
  itemSeed: string,
  organParams: LathePropulsionParams,
): number {
  if (t <= 0 || t >= 1) return 0;
  const phase = hashUnit(itemSeed) * Math.PI * 2;
  const envelope = Math.pow(Math.sin(Math.PI * t), organParams.profileEasing);
  let chamber = 0;
  for (const [stationIndex, value] of FIBONACCI_STATIONS.entries()) {
    const station = value / 14;
    const mirrored = stationIndex % 2 === 0 ? station : 1 - station;
    const width = 0.018 + station * 0.012;
    const distance = (t - mirrored) / width;
    chamber += Math.exp(-distance * distance)
      * (0.14 + station * 0.13)
      * (0.9 + 0.1 * Math.sin(phase + stationIndex * 2.4));
  }
  const turbineRipple = 0.045 * Math.sin(t * Math.PI * 16 + phase) * envelope;
  const middle = Math.exp(-Math.pow((t - 0.5) / 0.28, 2));
  const smoothBody = 0.64 + organParams.middleBulge * 0.34 * middle;
  return Math.max(0, maxRadius * envelope * (
    smoothBody + (chamber + turbineRipple) * organParams.chamberDetail
  ));
}

function validateParams(params: LathePropulsionParams): void {
  if (params.axisAlignment !== "ventral-fold" && params.axisAlignment !== "shell-endpoints") {
    throw new RangeError("Lathe axis alignment is invalid.");
  }
  if (!Number.isFinite(params.foldSeating) || params.foldSeating < 0 || params.foldSeating > 1) {
    throw new RangeError("Lathe fold seating must be between 0 and 1.");
  }
  if (!Number.isFinite(params.diameter) || params.diameter < 0.5 || params.diameter > 4) {
    throw new RangeError("Lathe diameter must be between 0.5 and 4.");
  }
  if (!Number.isFinite(params.middleBulge) || params.middleBulge < 0.4 || params.middleBulge > 4) {
    throw new RangeError("Lathe middle bulge must be between 0.4 and 4.");
  }
  if (!Number.isFinite(params.chamberDetail) || params.chamberDetail < 0 || params.chamberDetail > 1) {
    throw new RangeError("Lathe chamber detail must be between 0 and 1.");
  }
  if (!Number.isFinite(params.opacity) || params.opacity < 0 || params.opacity > 1) {
    throw new RangeError("Lathe opacity must be between 0 and 1.");
  }
  if (!Number.isFinite(params.filamentOpacity) || params.filamentOpacity < 0.35 || params.filamentOpacity > 1) {
    throw new RangeError("Lathe filament opacity must be between 0.35 and 1.");
  }
  if (!Number.isFinite(params.axisOffset) || params.axisOffset < -0.5 || params.axisOffset > 0.5) {
    throw new RangeError("Lathe axis offset must be between -0.5 and 0.5.");
  }
  if (!Number.isFinite(params.endpointInset) || params.endpointInset < 0 || params.endpointInset > 0.2) {
    throw new RangeError("Lathe endpoint inset must be between 0 and 0.2.");
  }
  if (!Number.isFinite(params.profileEasing) || params.profileEasing < 0.2 || params.profileEasing > 3) {
    throw new RangeError("Lathe profile easing must be between 0.2 and 3.");
  }
  if (!Number.isFinite(params.filamentClearance) || params.filamentClearance < 0 || params.filamentClearance > 0.25) {
    throw new RangeError("Lathe filament clearance must be between 0 and 0.25.");
  }
}

function shellEndpointAxisY(): number {
  return 0.11;
}

function ventralAxisY(params: CowryGeometryParams, foldEmphasis: number): number {
  const base = -params.shellHeight * 0.5 * params.ventralFlatness;
  return base + params.shellHeight * 0.1 * foldEmphasis;
}

function hashUnit(value: string): number {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0) / 4294967295;
}
